import i18n from 'i18next';
import { PaymentOptionPropertiesInterface } from './Interfaces';

const TranslationPath = 'Services:Exports:Constants';

/**
 * sessionStorage key the Playwright suite sets to choose which seeded company a localhost dev
 * server resolves, overriding the build-time `VITE_DEFAULT_LOCAL_SUBDOMAIN`.
 *
 * Read by `DomainResource.getFranchiseResolverParams` and only honoured when `VITE_EXPOSE_REDUX_STORE` is
 * 'true' — an env var set exclusively by `playwright.config.ts`'s webServer, never by a committed
 * .env. It lives here rather than in DomainResource so `e2e/support/constants.ts` stays the single
 * src/ -> Node import seam that `import-boundary.spec.ts` guards.
 *
 * NOTE: deliberately NOT a `SessionStorageReservedKey`. Those are composed as
 * `company-{id}-{key}` by `useSessionStorage`, and this one has to be readable before any company
 * id exists.
 */
export const E2E_COMPANY_SLUG_OVERRIDE_KEY = 'e2e-company-slug';

export enum OTP_DELIVERY_CHANNELS {
  SMS = 'sms',
  EMAIL = 'email',
  WHATSAPP = 'whatsapp',
}

// error_code values the backend returns from POST /one-time-passwords.
export enum OTP_REQUEST_ERROR_CODE {
  // An existing account for this phone has no email on file; only support can attach one.
  EMAIL_NOT_ON_FILE = 'email_not_on_file',
}

// Mirrors the backend OtpVerificationFailureReason error_code returned by /login.
export enum OTP_VERIFICATION_FAILURE_REASON {
  INCORRECT_CODE = 'incorrect_code',
  EXPIRED_CODE = 'expired_code',
  TOO_MANY_ATTEMPTS = 'too_many_attempts',
  // Frontend-only buckets — the backend never sends these. They replace the old behaviour
  // of labelling every unmapped failure as `incorrect_code`, so Mixpanel can tell a genuine
  // wrong code apart from a new/omitted backend code (`unknown`) or a request that never
  // reached the server (`network_error`).
  UNKNOWN = 'unknown',
  NETWORK_ERROR = 'network_error',
}

export enum ORDERS_PAUSED_REASON {
  MANUAL = 'manual',
  INACTIVITY = 'inactivity',
}

export enum EventHelperTypes {
  LOG_OUT = 'log_out',
  DISPLAY_INFO_MODAL = 'display_info_modal',
  SHOW_MENU_ITEMS_MODAL = 'show_menu_modal',
  TOGGLE_PAYMENT_METHOD = 'toggle_payment_method',
  NO_INTERNET = 'no_internet',
}

export enum COUPON_TYPES {
  REWARD = 1,
  CASHBACK = 2,
  PROMO_CODE = 3,
  GIFT_CARD = 4,
}

export enum PROMO_CODE_DISCOUNT_TYPES {
  FIXED_AMOUNT = 'fixed_amount',
  PERCENTAGE = 'percentage',
}

export enum ORDER_METHODS {
  PICKUP = 'pickup',
  DELIVERY = 'delivery',
  DINE_IN = 'dine_in',
  ROOM_SERVICE = 'room_service',
}

export enum ORDER_PAYMENT_STATUSES {
  PENDING = 'pending',
  PAID = 'paid',
  PARTIALLY_REFUNDED = 'partially_refunded',
  REFUNDED = 'refunded',
}

/**
 * Derived discriminator on the payment-status endpoint: unlike payment_status
 * it can distinguish a FAILED/abandoned payment from one still in flight
 * (payment_status stays 'pending' for both). Derived from the order's latest
 * payment attempt, so a live retry is never reported failed.
 */
export enum ORDER_PAYMENT_STATES {
  AWAITING_PAYMENT = 'awaiting_payment',
  FAILED = 'failed',
  PAID = 'paid',
  PARTIALLY_REFUNDED = 'partially_refunded',
  REFUNDED = 'refunded',
}

export enum ORDER_FULFILLMENT_STATUSES {
  CREATED = 'created',
  REVIEWED = 'reviewed',
  ACCEPTED = 'accepted',
  PREPARING = 'preparing',
  READY = 'ready',
  ON_DELIVERY = 'on_delivery',
  PICKED = 'picked',
  REJECTED = 'rejected',
}

export enum SCREENS {
  NOT_FOUND_SCREEN = '404',
  HOME_SCREEN = '/',
  CHECKOUT = 'checkout',
  PAYMENT = 'payment',
  BASKET = 'basket',
  ORDER_DETAILS = 'order-details',
  TERMS_AND_CONDITIONS = 'terms-and-conditions',
  PRIVACY_POLICY = 'privacy-policy',
  COOKIE_POLICY = 'cookie-policy',
  IMPRESSUM = 'impressum',
  UPLOAD_GOOGLE_REVIEW_SCREEN = 'upload-google-review',
  REWARDS_SCREEN = 'rewards',

  GIFT_CARDS_CHECKOUT = 'gift-cards/checkout',
  GIFT_CARDS_ORDER_DETAILS = 'gift-cards/order-details',
  GIFT_CARDS_TERMS_AND_CONDITIONS = 'gift-cards/terms-and-conditions',
}

export enum PaymentMethod {
  Cash = 'cash',
  ApplePay = 'apple_pay',
  GooglePay = 'google_pay',
  PaypalStandalone = 'paypal_standalone',
  Card = 'card',
  Paypal = 'paypal',
  Sofort = 'sofort',
  Giropay = 'giropay',
  PayByBank = 'pay_by_bank',
  SepaDebit = 'sepa_debit',
  BacsDebit = 'bacs_debit',
  Ideal = 'ideal',
  Bancontact = 'bancontact',
  Eps = 'eps',
  P24 = 'p24',
  Blik = 'blik',
  Twint = 'twint',
  Venmo = 'venmo',
  Wero = 'wero',
  Klarna = 'klarna',
  Trustly = 'trustly',
  Pix = 'pix',
  Nupay = 'nupay',
  Link = 'link',
  /**
   * A type Stripe charged through the Payment Element that this enum has no case for yet. The
   * backend keeps the raw type on the transaction; this is what fees, receipts and the order page
   * key on until the type gets a case of its own.
   */
  StripeOther = 'stripe_other',
}

/**
 * Local methods only Spreedly takes: the customer authorises at their bank or wallet and comes
 * back. Offered only when the backend lists them in `company.supported_payment_methods`, which it
 * does only when one of the partner's gateways serves them for the restaurant. PayPal is offsite on
 * Spreedly too, but it is not listed here: Stripe also takes it (see `isSpreedlyOffsitePayment`).
 */
export const SPREEDLY_OFFSITE_METHODS: readonly PaymentMethod[] = [
  PaymentMethod.Ideal,
  PaymentMethod.Bancontact,
  PaymentMethod.Eps,
  PaymentMethod.P24,
  PaymentMethod.Blik,
  PaymentMethod.Twint,
  PaymentMethod.Venmo,
  PaymentMethod.Wero,
  PaymentMethod.Klarna,
  PaymentMethod.Trustly,
  PaymentMethod.Pix,
  PaymentMethod.Nupay,
];

/** Brazilian methods: the acquirer needs the payer's CPF, asked for at checkout and never stored. */
export const TAXPAYER_DOCUMENT_METHODS: readonly PaymentMethod[] = [PaymentMethod.Pix, PaymentMethod.Nupay];

export const requiresTaxpayerDocument = (method: PaymentMethod | string | null | undefined): boolean =>
  TAXPAYER_DOCUMENT_METHODS.includes(method as PaymentMethod);

export const isSpreedlyOffsiteMethod = (method: PaymentMethod | string | null | undefined): boolean =>
  SPREEDLY_OFFSITE_METHODS.includes(method as PaymentMethod);

/**
 * Whether a payment already routed to Spreedly goes offsite rather than through the card form:
 * the Spreedly-only methods, and PayPal from the partner's PayPal or Braintree connection.
 */
export const isSpreedlyOffsitePayment = (method: PaymentMethod | string | null | undefined): boolean =>
  isSpreedlyOffsiteMethod(method) || method === PaymentMethod.Paypal;

/**
 * The card brands the app has artwork for. Gateways spell brands differently (Stripe `mastercard`,
 * Adyen `mc`, Spreedly `master`), so resolve raw strings through `normalizeCardBrand` rather than
 * comparing against these values directly.
 */
export enum PaymentMethodBrand {
  Visa = 'visa',
  MasterCard = 'master_card',
  AmericanExpress = 'american_express',
  Discover = 'discover',
  DinersClub = 'diners_club',
  Jcb = 'jcb',
  UnionPay = 'unionpay',
  Maestro = 'maestro',
  Dankort = 'dankort',
}

export const PAYMENT_METHODS = (): PaymentOptionPropertiesInterface[] => [
  {
    id: 3,
    payment_method_id: PaymentMethod.ApplePay,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Apple Pay',
    display_name: 'Apple Pay',
    is_default: false,
    is_saved: false,
  },
  {
    id: 4,
    payment_method_id: PaymentMethod.GooglePay,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Google Pay',
    display_name: 'Google Pay',
    is_default: false,
    is_saved: false,
  },
  {
    id: 1,
    payment_method_id: PaymentMethod.Card,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Credit Card',
    display_name: i18n.t(TranslationPath + '.payment_methods.card'),
    is_default: false,
    is_saved: false,
  },
  {
    id: 9,
    payment_method_id: PaymentMethod.PayByBank,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Pay by bank',
    display_name: i18n.t(TranslationPath + '.payment_methods.pay_by_bank'),
    is_default: false,
    is_saved: false,
  },
  {
    id: 6,
    payment_method_id: PaymentMethod.Giropay,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Giro Pay',
    display_name: i18n.t(TranslationPath + '.payment_methods.giropay'),
    is_default: false,
    is_saved: false,
  },
  {
    id: 5,
    payment_method_id: PaymentMethod.Sofort,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Sofort',
    display_name: i18n.t(TranslationPath + '.payment_methods.sofort'),
    is_default: false,
    is_saved: false,
  },
  {
    id: 10,
    payment_method_id: PaymentMethod.Ideal,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'iDEAL | Wero',
    display_name: 'iDEAL | Wero',
    is_default: false,
    is_saved: false,
  },
  {
    id: 11,
    payment_method_id: PaymentMethod.Bancontact,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Bancontact',
    display_name: 'Bancontact',
    is_default: false,
    is_saved: false,
  },
  {
    id: 12,
    payment_method_id: PaymentMethod.Eps,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'EPS',
    display_name: 'EPS',
    is_default: false,
    is_saved: false,
  },
  {
    id: 13,
    payment_method_id: PaymentMethod.P24,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Przelewy24',
    display_name: 'Przelewy24',
    is_default: false,
    is_saved: false,
  },
  {
    id: 14,
    payment_method_id: PaymentMethod.Blik,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'BLIK',
    display_name: 'BLIK',
    is_default: false,
    is_saved: false,
  },
  {
    id: 15,
    payment_method_id: PaymentMethod.Twint,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'TWINT',
    display_name: 'TWINT',
    is_default: false,
    is_saved: false,
  },
  {
    id: 16,
    payment_method_id: PaymentMethod.Venmo,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Venmo',
    display_name: 'Venmo',
    is_default: false,
    is_saved: false,
  },
  {
    id: 17,
    payment_method_id: PaymentMethod.Wero,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Wero',
    display_name: 'Wero',
    is_default: false,
    is_saved: false,
  },
  {
    id: 18,
    payment_method_id: PaymentMethod.Klarna,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Klarna',
    display_name: 'Klarna',
    is_default: false,
    is_saved: false,
  },
  {
    id: 19,
    payment_method_id: PaymentMethod.Trustly,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Trustly',
    display_name: 'Trustly',
    is_default: false,
    is_saved: false,
  },
  {
    id: 20,
    payment_method_id: PaymentMethod.Pix,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Pix',
    display_name: 'Pix',
    is_default: false,
    is_saved: false,
  },
  {
    id: 21,
    payment_method_id: PaymentMethod.Nupay,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'NuPay',
    display_name: 'NuPay',
    is_default: false,
    is_saved: false,
  },
  {
    id: 2,
    payment_method_id: PaymentMethod.Paypal,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'PayPal',
    display_name: 'PayPal',
    is_default: false,
    is_saved: false,
  },
  // {
  //   id: 7,
  //   payment_method_id: PaymentMethod.SepaDebit,
  //   payment_method_brand: null,
  //   payment_method_details: null,
  //   name: "Sepa Debit",
  //   display_name: i18n.t(TranslationPath + ".payment_methods.iban"),
  //   is_default: false,
  //   is_saved: false,
  // },
  {
    id: 8,
    payment_method_id: PaymentMethod.Cash,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Cash',
    display_name: i18n.t(TranslationPath + '.payment_methods.cash'),
    is_default: false,
    is_saved: false,
  },
  // Payment Element only: never in the backend's supported list, so never a row of this shop's
  // own; listed so a transaction charged this way still resolves to a name and an icon.
  {
    id: 22,
    payment_method_id: PaymentMethod.Link,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Link',
    display_name: 'Link',
    is_default: false,
    is_saved: false,
  },
  {
    id: 23,
    payment_method_id: PaymentMethod.StripeOther,
    external_payment_method_id: null,
    payment_method_brand: null,
    payment_method_details: null,
    name: 'Other',
    display_name: i18n.t(TranslationPath + '.payment_methods.stripe_other'),
    is_default: false,
    is_saved: false,
  },
];

export enum PROMO_CODE_TYPES {
  ONE_TIME_PROMO_CODE = 'single_use',
  MULTI_PROMO_CODE = 'multi_use',
}

export enum PUSHER_EVENTS {
  ORDER_UPDATED = 'order.updated',
  ORDER_PAID = 'order.paid',
  ORDER_FAILED = 'order.failed',
  TRANSACTION_PAID = 'transaction.paid',
  REFUND_CREATED = 'refund.created',
  ORDERS_PAUSED = 'orders.paused',
  ORDERS_UNPAUSED = 'orders.unpaused',
}

export enum SCREEN_TYPES {
  DESKTOP = 'Desktop',
  TABLET = 'Tablet',
  MOBILE = 'Mobile',
}

export enum APP_STATE_REDUCER_TYPES {
  TOGGLE_LOGIN_MODAL = 'toggle_login_modal',
  TOGGLE_INFO_MODAL = 'toggle_info_modal',
  TOGGLE_MENU_ITEM_MODAL = 'toggle_menu_item_modal',
  SAVE_MENU = 'save_menu',
  TOGGLE_ORDER_METHOD_MODAL = 'toggle_order_method_modal',
  TOGGLE_OTP_VERIFICATION_MODAL = 'toggle_otp_verification_modal',
  TOGGLE_RESTAURANT_CLOSED_MODAL = 'toggle_restaurant_closed_modal',
  TOGGLE_ORDER_BASKET_MODAL = 'toggle_order_basket_modal',
  TOGGLE_ELIGIBLE_OFFERS_MODAL = 'toggle_eligible_offers_modal',
  TOGGLE_OFFER_MODAL = 'toggle_offer_modal',
  TOGGLE_FREE_ITEMS_MODAL = 'toggle_free_items_modal',
  TOGGLE_MOBILE_APP_SIGNUP_BONUS_MODAL = 'toggle_mobile_app_signup_bonus_modal',
  TOGGLE_ADD_PROMO_OR_GIFT_CARD_MODAL = 'toggle_add_promo_or_gift_card_modal',
  TOGGLE_BUY_GIFT_CARD_MODAL = 'toggle_buy_gift_card_modal',
}

export enum MODAL_SIZES {
  XS = 'lg:w-[540px] w-screen mx-md',
  SM = 'lg:w-[640px] w-screen mx-md',
  MD = 'lg:w-[740px] w-screen mx-md',
  LG = 'lg:w-[840px] w-screen mx-md',
  XL = 'lg:w-5/6 w-screen mx-md',
}

export enum ERROR_CODES {
  OTP_VERIFICATION_REQUIRED = 'otp_check_required',
  ACCOUNT_SWITCH_REQUIRED = 'account_switch_required',
  BLOCKED_FOR_FRAUD = 'blocked_for_fraud',
  ORDER_TIME_NOT_AVAILABLE = 'order_time_not_available',
  ORDER_SLOT_IS_TAKEN = 'order_slot_is_taken',
  ORDERS_PAUSED = 'orders_paused',
  COMPANY_CLOSED = 'company_closed',
  COMPANY_DELETED = 'company_deleted',
  PICKUP_CLOSED = 'pickup_closed',
  DELIVERY_CLOSED = 'delivery_closed',
  DELIVERY_UNAVAILABLE = 'delivery_unavailable',
  DELIVERY_THRESHOLD_EXCEEDED = 'delivery_threshold_exceeded',
  ITEMS_OUT_OF_STOCK = 'items_out_of_stock',
  PROMO_CODE_INVALID = 'promo_code_invalid',
  PROMO_CODE_THRESHOLD_NOT_REACHED = 'promo_code_usage_threshold_not_reached',
  ADDRESS_OUTSIDE_DELIVERY_ZONE = 'address_outside_delivery_zone',
  NO_DELIVERY_QUOTES_AVAILABLE = 'no_delivery_quotes_available',
  NO_COURIERS_AVAILABLE = 'no_couriers_available',
  ADDRESS_NOT_FOUND = 'address_not_found',
  DELIVERY_QUOTE_ALREADY_USED = 'delivery_quote_already_used',
  CARD_EXCEPTION = 'card_exception',
  ORDER_FULFILLMENT_FAILED = 'order_fulfillment_failed',
  CASH_PAYMENTS_NOT_SUPPORTED = 'cash_payments_not_supported',
  ITEMS_MENU_TIME_UNAVAILABLE = 'items_menu_time_unavailable',
  ROOM_SERVICE_LOCATION_UNAVAILABLE = 'room_service_location_unavailable',
  ROOM_SERVICE_UNAVAILABLE = 'room_service_unavailable',
}

export enum PUSHER_CONNECTION_STATES {
  INITIALIZED = 'initialized',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  UNAVAILABLE = 'unavailable',
  FAILED = 'failed',
  DISCONNECTED = 'disconnected',
}

export enum GOOGLE_REVIEW_STATUS_TYPES {
  UNDETERMINED = 0,
  IN_REVIEW = 1,
  APPROVED = 2,
  REJECTED = 3,
}

export enum ENVIRONMENTS {
  DEV = 'Development',
  STAGING = 'Staging',
  PROD = 'Production',
}

export const DEMO_ACCOUNT_PHONE_NUMBER = '+491780000000';
export const DEMO_ACCOUNT_OTP_CODE = '0000';

export enum REDUX_STORE_SLICES {
  APP_STATUS = 'appStatus',
  EVENT_HELPER = 'eventHelper',
  INITIAL_DATA = 'initialData',
  BASKET = 'basket',
  OFFER = 'offer',
  GIFT_CARDS_BASKET = 'giftCardsBasket',
  GIFT_CARD = 'giftCard',
  ORDER = 'order',
  PROFILE = 'profile',
  PAYMENT_FLOW = 'paymentFlow',
  PAYMENT_OPTIONS = 'paymentOptions',
  PRODUCT_LIKES = 'productLikes',
  MODAL = 'modal',
  ORDER_METHOD = 'order_method',
  MARKETING = 'marketing',
  MOBILE_APP = 'mobileApp',
  DISCOUNT_DISPLACEMENT = 'discountDisplacement',
}

export enum DELIVERY_ZONE_GEOMETRY_TYPES {
  CIRCLE = 'circle',
  POLYGON = 'polygon',
  ZIP = 'zip',
}

export const ORDER_PRODUCT_HEIGHT = 130;

export enum PRODUCT_IDENTIFIERS {
  VEGAN = 'vgn',
  VEGETARIAN = 'vgt',
  GLUTEN_FREE = 'gf',
  DAIRY_FREE = 'df',
  HALAL = 'hal',
  SPICY = 'h&s',
}

export enum EntryType {
  Product = 'product',
  Modifier = 'modifier',
}

export enum ModifierGroupType {
  SystemUpsell = 4,
}

export enum CategoryType {
  Default = 'default',
  Favourites = 'favourites',
  MostPopular = 'most_popular',
  Bogo = 'bogo',
  GiftCards = 'gift_cards',
  LastOrder = 'last_order',
}

// Synthetic categories are not part of the menu payload, so they get negative ids of their own.
export const BOGO_CATEGORY_ID = -1;
export const LAST_ORDER_CATEGORY_ID = -2;

export enum CuisineType {
  // Top/General
  Pizza = 'pizza',
  Burgers = 'burgers',
  ChickenFried = 'chicken_fried',
  Wings = 'wings',
  KebabDoner = 'kebab_doner',
  Sandwiches = 'sandwiches',
  Wraps = 'wraps',
  Salads = 'salads',
  Healthy = 'healthy',
  Bowls = 'bowls',
  Vegan = 'vegan',
  Vegetarian = 'vegetarian',
  GlutenFree = 'gluten_free',
  Halal = 'halal',

  // Asian & Pacific
  Sushi = 'sushi',
  Japanese = 'japanese',
  Ramen = 'ramen',
  UdonSoba = 'udon_soba',
  Korean = 'korean',
  KoreanFriedChicken = 'korean_fried_chicken',
  Chinese = 'chinese',
  Sichuan = 'sichuan',
  CantoneseDimSum = 'cantonese_dim_sum',
  Thai = 'thai',
  Vietnamese = 'vietnamese',
  Malaysian = 'malaysian',
  Indonesian = 'indonesian',
  Filipino = 'filipino',
  Poke = 'poke',
  HotPot = 'hot_pot',
  DumplingsGyozaBao = 'dumplings_gyoza_bao',

  // South Asian
  Indian = 'indian',
  Biryani = 'biryani',
  Pakistani = 'pakistani',
  SriLankan = 'sri_lankan',
  Nepalese = 'nepalese',

  // Middle East & Mediterranean
  Turkish = 'turkish',
  Lebanese = 'lebanese',
  Syrian = 'syrian',
  Israeli = 'israeli',
  Egyptian = 'egyptian',
  PersianIranian = 'persian_iranian',
  Iraqi = 'iraqi',
  Afghan = 'afghan',
  ShawarmaFalafel = 'shawarma_falafel',
  MezzeGrill = 'mezze_grill',
  Greek = 'greek',
  Mediterranean = 'mediterranean',

  // Europe
  Italian = 'italian',
  Pasta = 'pasta',
  French = 'french',
  SpanishTapas = 'spanish_tapas',
  Portuguese = 'portuguese',
  German = 'german',
  Austrian = 'austrian',
  Balkan = 'balkan',
  Hungarian = 'hungarian',
  Polish = 'polish',
  Czech = 'czech',
  Georgian = 'georgian',

  // Americas
  Mexican = 'mexican',
  TexMex = 'tex_mex',
  Peruvian = 'peruvian',
  Brazilian = 'brazilian',
  Argentinian = 'argentinian',
  Caribbean = 'caribbean',
  BBQAmerican = 'bbq_american',
  Steakhouse = 'steakhouse',
  AmericanGeneral = 'new_american',
  HotDogs = 'hot_dogs',

  // Seafood & Specialty
  Seafood = 'seafood',
  FishAndChips = 'fish_and_chips',
  SteamedFish = 'steamed_fish',

  // Breakfast, Bakery & Sweets
  BreakfastBrunch = 'breakfast_brunch',
  Bagels = 'bagels',
  Bakery = 'bakery',
  PastryDessert = 'pastry_dessert',
  WafflesCrepes = 'waffles_crepes',
  IceCreamGelato = 'ice_cream_gelato',
  Donuts = 'donuts',
  Cakes = 'cakes',

  // Beverage-led
  CoffeeCafe = 'coffee_cafe',
  BubbleTea = 'bubble_tea',
  JuiceSmoothies = 'juice_smoothies',
  Milkshakes = 'milkshakes',
  BarPub = 'bar_pub',
  WineBar = 'wine_bar',
  Cocktails = 'cocktails',

  // Other Concepts
  TapasMezeSmallPlates = 'tapas_meze_small_plates',
  Noodles = 'noodles',
  Soup = 'soup',
  Schnitzel = 'schnitzel',
  SoulFood = 'soul_food',
  ComfortFood = 'comfort_food',
  StreetFood = 'street_food',
  OrganicBio = 'organic_bio',
  KidsMenu = 'kids_menu',
}

export enum CashbackAccountOperationSource {
  WelcomeBonus = 'welcome_bonus',
  GoogleReviewReward = 'google_review_reward',
  RewardLoyaltyProgramConversion = 'reward_loyalty_program_conversion',
  Cashback = 'cashback',
  Refund = 'refund',
  CashbackUsage = 'cashback_usage',
  CashbackUsageRefund = 'cashback_usage_refund',
  ExpiredPointsVoid = 'expired_points_void',
  MobileAppSignupBonus = 'mobile_app_signup_bonus',
}

export enum TaxBehaviour {
  Inclusive = 'inclusive',
  Exclusive = 'exclusive',
}

export enum RoomServiceLocationType {
  RoomService = 'room_service',
  TableService = 'table_service',
}

export enum SmartPricingRuleModifierType {
  Absolute = 'absolute',
  FixedValue = 'fixed_value',
  Percentage = 'percentage',
}

export enum DEAL_TYPES {
  GOOGLE_REVIEW_PROGRAM = 'google_review_program',
  SMART_PRICING_RULE = 'smart_pricing_rule',
}

export enum OfferKindEnum {
  PercentageOffByProducts = 'percentage_off_by_products',
  PercentageOffByMinSpendValue = 'percentage_off_by_min_spend_value',
  AmountOffByProducts = 'amount_off_by_products',
  AmountOffByMinSpendValue = 'amount_off_by_min_spend_value',
  Bogo = 'bogo',
  BogoHalfPrice = 'bogo_half_price',
  FreeItemByProducts = 'free_item_by_products',
  FreeItemByMinSpendValue = 'free_item_by_min_spend_value',
}

export enum OfferTypeEnum {
  Bogo = 'bogo',
  PercentageOff = 'percentage_off',
  AmountOff = 'amount_off',
  FreeItem = 'free_item',
}

export enum DeliveryMode {
  Internal = 'internal',
  External = 'external',
}

export enum PaypalVariant {
  Direct = 'direct',
  ViaStripe = 'via_stripe',
  ViaSpreedly = 'via_spreedly',
}

export enum DeliveryProvider {
  Standalone = 'standalone',
  FirstDelivery = 'first_delivery',
  WoltDrive = 'wolt_drive',
}

export enum DeliveryCarrier {
  Uber = 'uber',
  Wolt = 'wolt',
}

export enum DeliveryStatus {
  Pending = 'pending',
  RunnerAssigned = 'runner_assigned',
  OnRoutePickup = 'en_route_pickup',
  ArrivedPickup = 'arrived_pickup',
  PickedUp = 'picked_up',
  OnRouteDropoff = 'en_route_dropoff',
  ArrivedDropoff = 'arrived_dropoff',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export enum DropoffAction {
  RingDoorBell = 'ring_door_bell',
  Sms = 'sms',
}

export enum AuthGrant {
  OneTimePassword = 'one_time_password',
  NhasUserToken = 'nhas_user_token',
}

export enum AppWebViewMessageTypes {
  PaymentIntent = 'payment_intent',
  LoadStripe = 'load_stripe',
  AuthToken = 'auth_token',
  RequestPushNotification = 'request_push_notification',
  RequestAppRating = 'request_app_rating',
}

export enum OnlineShopWebViewMessageTypes {
  PaymentResult = 'payment_result',
  SetPushNotificationRequestStatus = 'set_push_notification_request_status',
  OpenModal = 'open_modal',
  Navigate = 'navigate',
  AppReviewRequested = 'app_review_requested',
}

export enum PaymentStatus {
  Pending = 'pending',
  Succeeded = 'succeeded',
  Failed = 'failed',
}

/**
 * `PaymentResult.error.code` values the checkout hooks branch on. A customer backing out of a
 * 3DS challenge is reported as Failed — the order is abandoned — but it is not an error to alert on.
 */
export enum PaymentResultErrorCode {
  Cancelled = 'cancelled',
  /** The bank or wallet refused: the customer's answer, logged as a breadcrumb like a cancel. */
  Declined = 'declined',
  /** The page the payment sheet was on is gone: nobody is there to tell, so nothing is shown. */
  Abandoned = 'abandoned',
}

export enum DeviceOS {
  Android = 'android',
  iOS = 'ios',
}

export enum OrderFulfillmentScope {
  Active = 'active',
  Cancelled = 'cancelled',
  Complete = 'complete',
}

export enum PushNotificationStatus {
  Granted = 'granted',
  Undetermined = 'undetermined',
  Denied = 'denied',
}

export enum ThemeMode {
  Light = 'light',
  Dark = 'dark',
}

/**
 * How the captcha is rendered. `iframe` loads the widget from the captcha wrapper, so the vendor
 * only ever sees the wrapper's host: a shop embedded in a restaurant website, or a restaurant
 * served under a partner's own root, runs on a hostname nobody registered with Turnstile or hCaptcha.
 */
export enum CaptchaMode {
  Direct = 'direct',
  Iframe = 'iframe',
}

export enum LoginBonusKind {
  MobileAppSignupBonus = 'mobile_app_signup_bonus',
  WelcomeBonus = 'welcome_bonus',
}

export enum ProductAdditives {
  AcesulfamK = 'acesulfame_k',
  AceSucralose = 'ace_sucralose',
  Aesculin = 'aesculin',
  Agar = 'agar',
  Anthocyanin = 'anthocyanin',
  Antioxidant = 'antioxidant',
  AntioxidantAscorbicAcid = 'antioxidant_ascorbic_acid',
  AntioxidantTocopherols = 'antioxidant_tocopherols',
  Flavor = 'flavor',
  Flavors = 'flavors',
  AscorbicAcid = 'ascorbic_acid',
  Aspartame = 'aspartame',
  OysterSauce = 'oyster_sauce',
  BakingAid = 'baking_aid',
  BakingPowder = 'baking_powder',
  BalsamicVinegar = 'balsamic_vinegar',
  BalsamicCream = 'balsamic_cream',
  BasilPesto = 'basil_pesto',
  BenzoicAcid = 'benzoic_acid',
  BetaCarotene = 'beta_carotene',
  Betain = 'betain',
  BeetrootRed = 'beetroot_red',
  Beeswax = 'beeswax',
  Bitters = 'bitters',
  BlueCuracao = 'blue_curacao',
  BlueCuracaoSyrup = 'blue_curacao_syrup',
  BrandyVinegar = 'brandy_vinegar',
  Broth = 'broth',
  Caffeine = 'caffeine',
  Calcium = 'calcium',
  Caramel = 'caramel',
  CaramelColor = 'caramel_color',
  CaramelE150d = 'caramel_e150d',
  Carotene = 'carotene',
  CayennePepper = 'cayenne_pepper',
  Chili = 'chili',
  ChiliDressing = 'chili_dressing',
  ChiliMayonnaise = 'chili_mayonnaise',
  ChiliOil = 'chili_oil',
  Quinine = 'quinine',
  Chlorophyll = 'chlorophyll',
  CitricAcid = 'citric_acid',
  CitrusAcid = 'citrus_acid',
  CarbonDioxide = 'carbon_dioxide',
  CocktailSauce = 'cocktail_sauce',
  Curry = 'curry',
  CurryPaste = 'curry_paste',
  CurryPowder = 'curry_powder',
  CurryCreamSauce = 'curry_cream_sauce',
  Fragrance = 'fragrance',
}

export enum LogoSize {
  Logo = 512,
  Favicon = 192,
}

export enum GiftCardRedemptionMode {
  Online = 'online',
  OnlineAndStore = 'online_and_store',
}

export enum PaymentGateway {
  Stripe = 'stripe',
  Adyen = 'adyen',
  Spreedly = 'spreedly',
}

/**
 * How a restaurant came onto the platform — `companies.restaurant_source` on the backend, stored at
 * creation. Sent with every Mixpanel event as the `restaurant_source` super property.
 */
export enum RestaurantSource {
  FaDirect = 'fa_direct',
  GloriafoodMigration = 'gloriafood_migration',
  Partner = 'partner',
}

export enum OrderType {
  Default = 'default',
  GiftCard = 'gift_card',
}

/**
 * Fields whose draft 422 the checkout form already renders as an inline error under the input
 * (CustomerInfoSection, InvoiceInfoSection — both read `validationErrors`).
 *
 * A rejection on one of these is not a fault: it IS the form's server-side validation. Every
 * keystroke in the email field is persisted to `cached_order`, so any draft that fires while the
 * address is half-typed carries `customer_email: "welb"` and is refused — working as designed.
 */
export const CUSTOMER_INPUT_KEYS = [
  'customer_first_name',
  'customer_last_name',
  'customer_email',
  'customer_phone_number',
  'invoice_email',
];
